import { useState, useEffect } from "react";
import { Button, InlineAlert, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import CurrencyInput from "../../components/BaseComponents/Forms/CurrencyInput";
import Input from "../../components/BaseComponents/Forms/Input";
import Label from "../../components/BaseComponents/Label";
import useApi from "../../hooks/useApi";
import { currencyToCents, getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type CompanyProjectEditModalProps = {
    open?: boolean;
    onClose: () => void,
    onEdited: () => void,
    projectId: string;
    projectName: string;
    projectCode: string;
    projectValue: number;
}

function CompanyProjectEditModal({open=false, onClose, projectId, projectName, projectCode, projectValue, onEdited}: CompanyProjectEditModalProps) {
    const [loading, setLoading] = useState(false);
    // Issue #884: onSave previously had no catch at all, so a rejecting write (including the
    // now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE notify-only codes - #857 item 6)
    // produced an unhandled rejection and left `loading` stuck true (spinner forever) with no
    // visible feedback - the modal never closed either way, but silently.
    const [error, setError] = useState('');
    const api = useApi();
    const [name, setName] = useState(projectName);
    const [code, setCode] = useState(projectCode);
    const [revenue, setRevenue] = useState(projectValue);

    // Sync prop changes to state
    useEffect(() => {
        setName(projectName)
        setCode(projectCode)
        setRevenue(projectValue)
    }, [projectName, projectCode, projectValue])

    const onSave = async () => {
        setLoading(true)
        setError('')
        try {
            await api.put(`/projects/${projectId}/`, {
                _csrf: await getCSRFToken(),
                project: { name, revenue }
            })
        } catch (err: any) {
            // Standard pattern (see DeleteTrainingSessionModal/AdvisorsTable) - a visible,
            // existing-shape message, not a raw/logged error. The modal stays open (unlike
            // success) so the admin can retry or cancel without losing their edits.
            setError(getApiErrorMessage(err, 'Failed to update this project. Please try again.'))
            return
        } finally {
            setLoading(false)
        }
        onEdited();
        onClose()
    }

    return (
        <CanonicalModal
            open={open}
            onClose={onClose}
            size="small"
            title="Edit project"
            actions={
                loading ? null : (
                    <>
                        <Button type="Primary" text="Update" onClick={onSave} additionalClass="mr-5" />
                        <Button type="Outlined" text="Cancel" onClick={onClose} />
                    </>
                )
            }
        >
            { error && <InlineAlert type='error' className='mb-4'>{ error }</InlineAlert> }
            { loading ? (
                <LoadingSpinner />
            ) : (
                <div>
                    <Input
                        label="Project name"
                        type="text"
                        name="name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                    <Input
                        label="Unique project code"
                        type="text"
                        name="code"
                        disabled
                        value={code}
                        onChange={e => setCode(e.target.value)}
                    />
                    <Label className="block">Project value (total sales/revenues)</Label>

                    <CurrencyInput name="revenue" defaultValue={revenue} onChange={value => setRevenue(currencyToCents(value))}/>
                </div>
            )}
        </CanonicalModal>
    )
}

export default CompanyProjectEditModal;
